import type { SvgIconComponent } from "@mui/icons-material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import type { LogoKind } from "../../api/types";

export type PickerKind = LogoKind | "invoice" | "auto-transaction";

export const ENTITY_LABELS: Record<PickerKind, string> = {
  source: "Source",
  group: "Group",
  category: "Category",
  "expense-type": "Expense Type",
  payee: "Payee",
  invoice: "Invoice",
  "auto-transaction": "Auto-transaction rule",
};

export type IconAvatarConfig = {
  Component: SvgIconComponent;
  /** MUI `sx` color token applied to the icon. */
  color: string;
};

/**
 * Kinds whose entities have no fetchable logo but do have a meaningful
 * one-icon-per-kind avatar (e.g. invoice). EntityAvatar uses this map to
 * render an icon-only avatar instead of hitting `/api/logo/…`.
 */
export const ICON_AVATARS: Partial<Record<PickerKind, IconAvatarConfig>> = {
  invoice: { Component: ReceiptLongIcon, color: "text.secondary" },
};

/**
 * Kinds that render with no avatar at all — the picker omits the leading
 * icon slot in both the trigger and the row, so the list is just names.
 * Right answer when the entities don't naturally carry a visual identity
 * and a per-kind icon would just be a column of identical glyphs.
 */
const KINDS_WITHOUT_AVATAR: ReadonlySet<PickerKind> = new Set<PickerKind>([
  "auto-transaction",
]);

const LOGO_KINDS: ReadonlySet<PickerKind> = new Set<PickerKind>([
  "source",
  "group",
  "category",
  "expense-type",
  "payee",
]);

/** Type guard: the kind has a fetchable logo at `/api/logo/{kind}/{id}`. */
export const isLogoKind = (kind: PickerKind): kind is LogoKind =>
  LOGO_KINDS.has(kind);

/** Whether the picker should render any avatar at all for this kind. */
export const hasAvatar = (kind: PickerKind): boolean =>
  !KINDS_WITHOUT_AVATAR.has(kind);
